import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'components/button/button.component';
import Icon from 'components/icon/icon.component';
import { ITelegramSubscription } from 'interfaces/telegram.interface';
import {
  createTelegramLink,
  disconnectTelegram,
  getTelegramStatus,
  getTelegramSubscriptions,
} from 'requests/telegram.request';
import './telegram-card.style.scss';

const TelegramCard: React.FC = () => {
  const { t } = useTranslation();

  const [configured, setConfigured] = useState<boolean | null>(null);
  const [subscriptions, setSubscriptions] = useState<ITelegramSubscription[]>([]);
  const [deepLink, setDeepLink] = useState<string | null>(null);
  const [groupLink, setGroupLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    getTelegramStatus()
      .then((res) => {
        setConfigured(res.configured);
        return res.configured ? getTelegramSubscriptions() : [];
      })
      .then((subs) => setSubscriptions(subs))
      .catch(() => setConfigured(false));
  }, []);

  useEffect(() => refresh(), [refresh]);

  const generate = () => {
    setBusy(true);
    createTelegramLink()
      .then((res) => {
        setDeepLink(res.deepLink ?? null);
        setGroupLink(res.groupDeepLink ?? null);
      })
      .catch(() => toast.error(t('telegram.linkFailed')))
      .finally(() => setBusy(false));
  };

  const disconnect = (id: number) => {
    setBusy(true);
    disconnectTelegram(id)
      .then(() => {
        toast.success(t('telegram.disconnected'));
        refresh();
      })
      .catch(() => toast.error(t('telegram.disconnectFailed')))
      .finally(() => setBusy(false));
  };

  const copyLink = () => {
    if (!deepLink) return;
    navigator.clipboard
      .writeText(deepLink)
      .then(() => toast.success(t('telegram.linkCopied')))
      .catch(() => undefined);
  };

  if (configured === null) {
    return null;
  }

  return (
    <section className="telegramCard">
      <h2 className="telegramCard__title">
        <Icon name="Send" size={18} />
        {t('telegram.title')}
      </h2>

      {!configured ? (
        <p className="telegramCard__muted">{t('telegram.notConfigured')}</p>
      ) : (
        <>
          <p className="telegramCard__muted">{t('telegram.description')}</p>

          {subscriptions.length > 0 && (
            <ul className="telegramCard__list">
              {subscriptions.map((s) => (
                <li key={s.id} className="telegramCard__item">
                  <div>
                    <strong>{s.title || `chat ${s.chatId}`}</strong>
                    <span className="telegramCard__muted">
                      {s.dailyDigestEnabled
                        ? ` · ${t('telegram.digestOn')}`
                        : ` · ${t('telegram.digestOff')}`}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="telegramCard__unlink"
                    onClick={() => disconnect(s.id)}
                    disabled={busy}
                  >
                    {t('telegram.disconnect')}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {deepLink ? (
            <div className="telegramCard__link">
              {/* One token, two ways to spend it. A ?start= link always opens a private
                  chat, so adding the bot to a group needs the ?startgroup= form - that
                  is the step people get stuck on. */}
              <div className="telegramCard__choice">
                <div>
                  <strong>{t('telegram.forMe')}</strong>
                  <p className="telegramCard__muted">{t('telegram.forMeHint')}</p>
                  <Button onClick={() => window.open(deepLink, '_blank', 'noopener')} primary>
                    {t('telegram.open')}
                  </Button>
                </div>
                {groupLink && (
                  <div>
                    <strong>{t('telegram.forGroup')}</strong>
                    <p className="telegramCard__muted">{t('telegram.forGroupHint')}</p>
                    <Button onClick={() => window.open(groupLink, '_blank', 'noopener')} secondary>
                      {t('telegram.addToGroup')}
                    </Button>
                  </div>
                )}
              </div>

              <div className="telegramCard__linkActions">
                <Button onClick={copyLink} secondary>
                  {t('telegram.copy')}
                </Button>
              </div>
              <p className="telegramCard__muted">{t('telegram.linkHint')}</p>
            </div>
          ) : (
            <Button onClick={generate} disabled={busy} primary>
              {t('telegram.connect')}
            </Button>
          )}
        </>
      )}
    </section>
  );
};

export default TelegramCard;
